import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { ValidateCallOrderRequest } from '@/store/orders/call/state.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';

export type ValidateCallOrderBody = {
  isGtc: boolean;
  expiryDay: string | undefined;
  expiryTime: string | undefined;
  bdrId: string;
  ccyPair: string;
  limitPrice?: string;
  base64Emails: string;
  localTimeWithOffSet: number;
};

export const validateCallOrder = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ValidateOrderResponse, ValidateCallOrderRequest>({
    query: (request) => ({
      url: '/oms/validate/Call',
      method: 'POST',
      body: generateBody(request),
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformErrorResponse: (errorResponse): ValidateOrderResponse | FetchBaseQueryError => {
      if (errorResponse.status === 400) {
        if (typeof errorResponse.data === 'string') {
          const data = JSON.parse(errorResponse.data);

          if (typeof data === 'object' && 'validationResponseDetailContent' in data) {
            return data as ValidateOrderResponse;
          }
        }

        return errorResponse.data as ValidateOrderResponse;
      }

      return errorResponse;
    },
  });
};

const generateBody = (order: ValidateCallOrderRequest): ValidateCallOrderBody => {
  return {
    isGtc: order.expiry.type === 'GoodTillCancelled',
    expiryDay: order.expiry.type === 'ExpiryDate' ? order.expiry.date : undefined,
    expiryTime: order.expiry.type === 'ExpiryDate' ? order.expiry.time : undefined,
    bdrId: String(order.clientId),
    ccyPair: `${order?.currency1}/${order?.currency2}`,
    limitPrice: isDefined(order.limitPrice) ? String(order.limitPrice) : undefined,
    base64Emails: isDefined(order?.notificationEmails?.emails) ? btoa(order?.notificationEmails.emails.join(';')) : '',
    localTimeWithOffSet: order.clockOffset !== undefined ? Date.now() + order.clockOffset : 0,
  };
};
